import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { runNotificationAction } from '../../lib/notificationActions';
import { useNotification } from '../../stores/actions/StoreActions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const iconByType = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
  warning: AlertCircle,
} as const;

const isEditable = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

const Notification = (): JSX.Element => {
  const notification = useSelector((state: any) => state.notification);
  const { clearNotification } = useNotification();
  const actionId: string | undefined = notification.open ? notification.actionId : undefined;

  useEffect(() => {
    if (!notification.open || !notification.timeout) return;
    const id = window.setTimeout(() => clearNotification(), notification.timeout);
    return () => window.clearTimeout(id);
  }, [notification.open, notification.timeout, notification.message, notification.actionId, clearNotification]);

  const runAction = () => {
    if (!actionId) return;
    runNotificationAction(actionId);
    clearNotification();
  };

  // Command-Z undoes the last delete, unless the focus is in a field that has its own undo.
  useEffect(() => {
    if (!actionId) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z' && !isEditable(event.target)) {
        event.preventDefault();
        runNotificationAction(actionId);
        clearNotification();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [actionId, clearNotification]);

  if (!notification.open) {
    return <></>;
  }

  const type = (notification.type || 'info') as keyof typeof iconByType;
  const Icon = iconByType[type] ?? Info;

  return (
    <div className="pointer-events-none fixed inset-x-0 top-[64px] z-[1400] flex justify-center px-4">
      <Alert
        className={cn(
          // Stays visible over a dialog (z-1400) but never eats a tap meant
          // for it: the toast sits in a band across the top of the screen, so
          // anything under that band -- a dialog's own controls -- would stop
          // responding while a message was up. Only the buttons take pointer
          // events.
          'pointer-events-none w-full max-w-[420px] rounded-2xl border-border shadow-dialog backdrop-blur-md',
          type === 'success' && 'border-emerald-500/30 bg-emerald-950/90 text-foreground',
          type === 'error' && 'border-destructive/40 bg-red-950/90 text-foreground',
          type === 'warning' && 'border-amber-500/30 bg-amber-950/90 text-foreground',
          type === 'info' && 'border-white/10 bg-[#242424]/95 text-foreground'
        )}
      >
        <Icon className="size-4" />
        <AlertDescription className="flex items-center justify-between gap-2 pr-1 font-medium">
          <span className="min-w-0">{notification.message}</span>
          <span className="flex shrink-0 items-center">
            {actionId && notification.actionLabel ? (
              <button
                type="button"
                onClick={runAction}
                className="pointer-events-auto inline-flex min-h-8 items-center rounded-md px-2 text-sm font-semibold text-primary hover:text-primary/90 [@media(pointer:coarse)]:min-h-11"
              >
                {notification.actionLabel}
              </button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              className="pointer-events-auto size-7 shrink-0"
              onClick={() => clearNotification()}
              aria-label="Dismiss"
            >
              <X className="size-3.5" />
            </Button>
          </span>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default Notification;
